// ──────────────────────────────────────────────────────
// QCARE-IMAGE-UPLOAD-THUMBNAIL-PERSISTENCE-HOTFIX (2026-05-02)
//
// Browser-only helper that produces a small thumbnail data URL from an
// existing full-size data URL. Used by the chat surfaces (/m/q and the
// desktop ChatDrawer) so the user-uploaded image preview survives a
// page refresh — without storing the full-size base64 in localStorage,
// which would blow quota in 2-3 image messages.
//
// Strict scope:
//   • UI-only persistence preview. Recognition still uses the original
//     full-size data URL on the live request — never the thumbnail.
//   • No backend involvement. No upload. No DB. No env.
//   • Browser-only — guarded against SSR.
// ──────────────────────────────────────────────────────

export interface ThumbnailOptions {
    /** Max edge in CSS pixels. Default 160. */
    maxEdge?: number;
    /** JPEG quality 0..1. Default 0.55. */
    quality?: number;
    /** Hard cap on the produced data URL length (chars). Default 32_000
     *  (≈24KB raw bytes). If the encoded thumb exceeds this, return
     *  null so the caller can fall back to no-thumbnail behavior. */
    maxDataUrlLength?: number;
}

/** Produce a small JPEG thumbnail from a data URL.
 *
 *  Returns null when:
 *  - running in a non-browser environment (SSR),
 *  - the input is not a data URL we can render,
 *  - any decode/encode step throws,
 *  - the produced thumbnail exceeds maxDataUrlLength.
 *
 *  Errors are swallowed by design — the caller should fall back to the
 *  pre-fix behavior (no persistent thumbnail) rather than block the
 *  user from sending the message.
 */
export async function makeImageThumbnail(
    dataUrl: string,
    opts: ThumbnailOptions = {},
): Promise<string | null> {
    const maxEdge = opts.maxEdge ?? 160;
    const quality = opts.quality ?? 0.55;
    const maxLen = opts.maxDataUrlLength ?? 32_000;

    if (typeof window === 'undefined' || typeof document === 'undefined') return null;
    if (typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) return null;

    try {
        const img = await loadImage(dataUrl);
        const { width, height } = scaleToFit(img.naturalWidth || img.width, img.naturalHeight || img.height, maxEdge);
        if (width <= 0 || height <= 0) return null;

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;
        ctx.drawImage(img, 0, 0, width, height);

        const out = canvas.toDataURL('image/jpeg', quality);
        if (out.length > maxLen) return null;
        return out;
    } catch {
        return null;
    }
}

function loadImage(src: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image decode failed'));
        img.src = src;
    });
}

/** Compute target dimensions that fit inside an `maxEdge × maxEdge` box
 *  while preserving aspect ratio. Both dimensions are integers. */
export function scaleToFit(srcW: number, srcH: number, maxEdge: number): { width: number; height: number } {
    if (!Number.isFinite(srcW) || !Number.isFinite(srcH) || srcW <= 0 || srcH <= 0) {
        return { width: 0, height: 0 };
    }
    if (srcW <= maxEdge && srcH <= maxEdge) {
        return { width: Math.round(srcW), height: Math.round(srcH) };
    }
    const ratio = Math.min(maxEdge / srcW, maxEdge / srcH);
    return {
        width: Math.max(1, Math.round(srcW * ratio)),
        height: Math.max(1, Math.round(srcH * ratio)),
    };
}
